import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import api from '../services/api';

interface User {
    id: string;
    name: string;
    email: string;
    role: string;
}

interface AuthContextType {
    user: User | null;
    currency: string;
    loading: boolean;
    login: (token: string) => void;
    logout: () => void;
    isAuthenticated: boolean;
    setCurrency: (currency: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [currency, setCurrency] = useState<string>('EUR');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadUser = async () => {
            const token = localStorage.getItem('token');
            if (token) {
                try {
                    const res = await api.get('/auth/me');
                    setUser(res.data);

                    try {
                        const settingsRes = await api.get('/settings');
                        if (settingsRes.data && settingsRes.data.currency) {
                            setCurrency(settingsRes.data.currency);
                        }
                    } catch (err) {
                        console.error('Failed to load settings', err);
                    }
                } catch (err) {
                    console.error('Error loading user', err);
                    localStorage.removeItem('token');
                }
            }
            setLoading(false);
        };

        loadUser();
    }, []);

    const login = async (token: string) => {
        localStorage.setItem('token', token);
        try {
            const res = await api.get('/auth/me');
            setUser(res.data);

            try {
                const settingsRes = await api.get('/settings');
                if (settingsRes.data && settingsRes.data.currency) {
                    setCurrency(settingsRes.data.currency);
                }
            } catch (err) {
                console.error('Failed to load settings', err);
            }
        } catch (err) {
            localStorage.removeItem('token');
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        setUser(null);
        setCurrency('EUR');
    };

    return (
        <AuthContext.Provider value={{ user, currency, loading, login, logout, isAuthenticated: !!user, setCurrency }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
